import React, { FunctionComponent, useState, Suspense } from 'react'
import { Tours, TourProperties } from '@homevest/types/lead-groups'
import { Button, Select, Stack, Text, useToast, Spinner, Box } from '@chakra-ui/react'
import { useMutation } from '@tanstack/react-query'
import { startCase } from 'lodash'
import { tours } from '@homevest/utils'
import { useOwnedPortfolioHomesQuery } from 'graphql/generated'

import axios from 'lib/axios'
import {
  BaseFormField,
  DateTimeFormField,
  TextareaFormField
} from 'components/ChakraToolkit/FormField'
import { SearchDropdown } from 'components/ChakraToolkit/SearchDropdown'

const { TOUR_TYPES } = tours

type TourFormData = Tours & {
  note?: string | null
}

const PropertySearchDropdown: FunctionComponent<{
  onSelect: (propertyId: string) => void
}> = ({ onSelect }) => {
  const [{ data: portfolioHomesData }] = useOwnedPortfolioHomesQuery()

  return (
    <BaseFormField field='property_id' label='Property' isRequired errors={{ property_id: '' }}>
      <SearchDropdown
        data={
          portfolioHomesData
            ? portfolioHomesData.portfolio_homes.map((property) => {
                return `${property.id} - ${property.home.address.display_line_1}, ${property.home.address.display_line_1}`
              })
            : []
        }
        placeholder='Select a property...'
        formatDropdownItem={(result: string) => {
          const [_, address] = result.split(' - ')
          return <Text>{address}</Text>
        }}
        formatSelectedItem={(result: string) => {
          const [_, address] = result.split(' - ')
          return address
        }}
        action={(result: string) => {
          const [propertyId, _] = result.split(' - ')
          onSelect(propertyId)
        }}
      />
    </BaseFormField>
  )
}

const CreateTour: FunctionComponent<
  React.PropsWithChildren<{ leadGroupId: string; onClose: () => void }>
> = ({ leadGroupId, onClose }) => {
  const toast = useToast()
  const [tourData, setTourData] = useState<TourFormData>({
    lead_group_id: leadGroupId,
    start_at: new Date().toISOString(),
    status: 'complete',
    type: '',
    note: ''
  })
  const [tourPropertyData, setTourPropertyData] = useState<TourProperties>({
    tour_id: '',
    property_id: '',
    status: 'confirmed',
    tour_order: 0
  })

  const [tourErrors, setTourErrors] = useState<
    Record<keyof (TourFormData & TourProperties), string>
  >({} as Record<keyof (TourFormData & TourProperties), string>)

  const formIsValid = () => {
    const newErrors: Record<keyof (TourFormData & TourProperties), string> = { ...tourErrors }

    if (!tourData.type) {
      newErrors.type = 'Tour type is required'
    }
    if (!tourPropertyData.property_id) {
      newErrors.property_id = 'Property is required'
    }
    if (!tourData.start_at) {
      newErrors.start_at = 'Start at is required'
    }

    setTourErrors(newErrors)

    return !Object.values(newErrors).some((error) => error !== '')
  }

  const { mutate: createTour, isLoading } = useMutation({
    mutationFn: async (data: { tour: TourFormData; tourProperties: TourProperties }) => {
      return axios.post(`/admin/lead_groups/${leadGroupId}/tours`, data)
    },
    onSuccess: () => {
      toast({
        title: 'Tour Created',
        status: 'success',
        duration: 5000,
        position: 'bottom-right'
      })
      onClose()
    },
    onError: (error: any) => {
      toast({
        title: 'Error Creating Tour',
        description: error?.message || 'An unknown error occurred',
        status: 'error',
        duration: 5000,
        position: 'bottom-right'
      })
    }
  })

  const handleSubmit = () => {
    if (formIsValid()) {
      createTour({ tour: tourData, tourProperties: tourPropertyData })
    }
  }

  return (
    <Stack spacing={4}>
      <Suspense
        fallback={
          <Box position='relative' height='40px'>
            <Spinner
              position='absolute'
              top='50%'
              left='50%'
              transform='translate(-50%, -50%)'
              color='teal.500'
            />
          </Box>
        }
      >
        <PropertySearchDropdown
          onSelect={(propertyId) =>
            setTourPropertyData({ ...tourPropertyData, property_id: propertyId })
          }
        />
      </Suspense>
      <DateTimeFormField<TourFormData>
        field='start_at'
        label='Start At'
        isRequired
        errors={tourErrors}
        data={tourData}
        setData={setTourData}
      />
      <BaseFormField<TourFormData> field='type' label='Type' isRequired errors={tourErrors}>
        <Select
          value={tourData.type || ''}
          onChange={(e) => setTourData({ ...tourData, type: e.target.value })}
        >
          <option value=''>Select a tour type</option>
          {Object.values(TOUR_TYPES).map((type) => (
            <option key={type} value={type}>
              {startCase(type)}
            </option>
          ))}
        </Select>
      </BaseFormField>
      <TextareaFormField<TourFormData>
        field='note'
        label='Tour Feedback / Notes'
        isRequired={false}
        errors={tourErrors}
        data={tourData}
        setData={setTourData}
        textareaProps={{
          placeholder: 'Add tour feedback',
          rows: 4
        }}
      />
      <Button colorScheme='teal' size='sm' disabled={isLoading} onClick={handleSubmit}>
        Submit Tour
      </Button>
    </Stack>
  )
}

export default CreateTour
